import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, AlertCircle, CheckCircle2, PauseCircle, Search, X } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Tenant } from '@/types';

type StatusFilter = 'all' | 'active' | 'suspended';

export function Tenants() {
  const [tenants, setTenants]       = useState<Tenant[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const searchRef                   = useRef<HTMLInputElement>(null);
  const isSuperAdmin                = api.isSuperAdmin();

  useEffect(() => {
    api.listTenants()
      .then(setTenants)
      .catch((e) => setError(e instanceof ApiClientError ? e.message : 'Failed to load tenants'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenants.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (q === '') return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.tenant_id.toLowerCase().includes(q)
      );
    });
  }, [tenants, search, statusFilter]);

  const hasFilter = search.trim() !== '' || statusFilter !== 'all';

  function clearFilters() {
    setSearch('');
    setStatusFilter('all');
    searchRef.current?.focus();
  }

  return (
    <div className="max-w-5xl">
      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Tenants</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">Manage organisations and their usage quotas</p>
        </div>
        {isSuperAdmin && (
          <Link to="/tenants/new" className="btn-primary self-start sm:self-auto">
            <Plus size={16} className="mr-2" />
            Add Tenant
          </Link>
        )}
      </div>

      {/* ── Search + status filter bar ── */}
      {!loading && !error && tenants.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
          {/* Search input */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or tenant ID…"
              className="w-full pl-8 pr-8 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); searchRef.current?.focus(); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Status chips */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {(['all', 'active', 'suspended'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  statusFilter === s
                    ? s === 'active'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-transparent'
                      : s === 'suspended'
                      ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-transparent'
                      : 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 border-transparent'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {/* Result count + clear */}
          <div className="flex items-center gap-2 flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
            <span>
              <span className="font-semibold text-gray-600 dark:text-gray-300">{filtered.length}</span>
              {filtered.length !== tenants.length && <span> of {tenants.length}</span>}
              {' '}tenant{filtered.length !== 1 ? 's' : ''}
            </span>
            {hasFilter && (
              <button
                onClick={clearFilters}
                className="text-brand-600 dark:text-brand-400 hover:underline font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-gray-500 dark:text-gray-400 py-12 text-center">Loading…</div>}

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400 text-sm">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {!loading && !error && tenants.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Users size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 dark:text-white mb-1">No tenants yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Add your first organisation to start issuing widget tokens.</p>
          {isSuperAdmin && (
            <Link to="/tenants/new" className="btn-primary">
              <Plus size={15} className="mr-2" /> Add Tenant
            </Link>
          )}
        </div>
      )}

      {/* ── No results after filtering ── */}
      {!loading && !error && tenants.length > 0 && filtered.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Search size={36} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 dark:text-white mb-1">No tenants match</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Try adjusting your search or status filter.</p>
          <button onClick={clearFilters} className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
            Clear filters
          </button>
        </div>
      )}

      {/* ── Table ── */}
      {!loading && !error && filtered.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">Tenant</th>
                  <th className="px-4 py-3 text-left hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">Users</th>
                  <th className="px-4 py-3 text-right hidden lg:table-cell">Max / Day</th>
                  <th className="px-4 py-3 text-right hidden lg:table-cell">Max / Month</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">Quota / User</th>
                  <th className="px-4 py-3 text-right hidden sm:table-cell">Today's Requests</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {filtered.map((t) => {
                  const quotaPerUser = t.user_count
                    ? Math.floor(t.max_requests_per_day / t.user_count)
                    : t.max_requests_per_day;
                  return (
                    <tr key={t.tenant_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900 dark:text-white">
                          <HighlightMatch text={t.name} query={search} />
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
                          <HighlightMatch text={t.tenant_id} query={search} />
                        </div>
                        {/* Status badge inline on mobile */}
                        <div className="sm:hidden mt-1.5">
                          <StatusBadge status={t.status} />
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <StatusBadge status={t.status} />
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums hidden md:table-cell">
                        <span className={t.user_count === t.max_users ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-gray-900 dark:text-white'}>
                          {t.user_count ?? 0}
                        </span>
                        <span className="text-gray-400 dark:text-gray-500"> / {t.max_users}</span>
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                        {t.max_requests_per_day.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                        {t.max_requests_per_month.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums text-gray-700 dark:text-gray-300 hidden md:table-cell">
                        {quotaPerUser.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <TodayUsageCell used={t.requests_today ?? 0} limit={t.max_requests_per_day} />
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Link
                          to={`/tenants/${t.tenant_id}`}
                          className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium"
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Highlight matching text ──────────────────────────────────────────────────

function HighlightMatch({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-100 dark:bg-yellow-800/40 text-yellow-900 dark:text-yellow-200 rounded-sm px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function TodayUsageCell({ used, limit }: { used: number; limit: number }) {
  const pct     = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const isHigh  = pct >= 80;
  const isMid   = pct >= 50 && pct < 80;

  const barColor = isHigh
    ? 'bg-red-500 dark:bg-red-400'
    : isMid
    ? 'bg-amber-400 dark:bg-amber-300'
    : 'bg-brand-500 dark:bg-brand-400';

  const textColor = isHigh
    ? 'text-red-600 dark:text-red-400'
    : isMid
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-gray-900 dark:text-white';

  return (
    <div className="flex flex-col items-end gap-1 min-w-[90px]">
      <span className={`tabular-nums text-sm font-medium ${textColor}`}>
        {used.toLocaleString()}
        <span className="text-gray-400 dark:text-gray-500 font-normal"> / {limit.toLocaleString()}</span>
      </span>
      <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: Tenant['status'] }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
        <CheckCircle2 size={11} /> Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
      <PauseCircle size={11} /> Suspended
    </span>
  );
}
